import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {take} from "rxjs/operators";
import {User} from "@sentry/browser";
import {BuilderHistoryService} from "../../../../builder-history.service";
import {BuilderSectionsInteractionService} from "../../../../builder-sections-interaction.service";
import {ColorI} from "../../../../builder-v2.interface";
import {SwitcherIds} from "../../../website-customizer/wesite-customization-switcher.enum";
import {FormControl} from "@angular/forms";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {WEBSITE_CUSTOMIZATIONS} from "../../../website-customizer/wesite-customization-options.constants";
import {CustomizationThemesEnum} from "../../../website-customizer/customization-themes.enum";
import {SUPPORTED_FONTS_LIST} from "../../../website-customizer/supported-fonts-list.constants";
import {CommonBuilderDataService} from "../../../../services/common-builder-data.service";
import {VibeCodingFlowService} from "../../../../../shared/services/vibe-coding-flow.service";
import {Website} from "../../../../../websites/website/website";

@Component({
    selector: 'db-outline-design',
    templateUrl: './outline-generation-design.component.html',
    styleUrl: './outline-generation-design.component.scss',
    standalone: false
})
export class OutlineGenerationDesignComponent implements OnInit, OnChanges {
  @Input() popupMode: boolean = false;
  @Input() user: User;
  @Input() uniqueId: string;
  @Input() isEcommerce: boolean;
  @Input() website: Website;

  isMobile = false;
  selectedOption: any;
  fullOptionsList = [];
  optionsList: any[] = [];
  destroyRef = inject(DestroyRef);
  switcherIds = SwitcherIds;
  searchControl: FormControl<string> = new FormControl('');
  styles = {
    '--accentColor': '#283566',
    '--secondaryColor': '#895253',
    '--accentColorShade': '#2835660A',
    '--accentColorShade2': '#2835661A',
    '--mainFontFamily': 'Montserrat'
  };

  theme = WEBSITE_CUSTOMIZATIONS.themes[0].class;
  activeColor: string = this.builderInteractionsService.activeColor.value.title;
  activeTheme: string = this.builderInteractionsService.activeTheme.value;
  activeFont: string = this.builderInteractionsService.activeFont.value;
  switchers: any[];
  vibeCodingFlow: any;
  supportedFonts = SUPPORTED_FONTS_LIST;
  selectedFont: string;
  selectedColor: ColorI;
  selectedTheme: { title: string, class: string };
  optionsFontList: any[] = [];

  @Output() activeSwitcherIdChange = new EventEmitter<number>();
  @Output() onOptionSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  private _activeColor: ColorI = this.builderInteractionsService.activeColor.value;
  private _destroyRef = inject(DestroyRef);

  constructor(public builderHistoryService: BuilderHistoryService,
              public builderInteractionsService: BuilderSectionsInteractionService,
              public _builderCommonDataService: CommonBuilderDataService,
              private _vibeCodingFlowService: VibeCodingFlowService) {
  }

  ngOnInit() {
    this.vibeCodingFlow = this._vibeCodingFlowService.isVibeCodingFlow() || this.website?.isWVC;

    this.fullOptionsList = this.optionsList;
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.optionsList = this.fullOptionsList.filter((option: string) => option.toLowerCase().startsWith(value.toLowerCase()));
      });
    this.isEcommerce && this.builderInteractionsService.activeTheme.next('Classic');
    this._listenDefaultColorChange();
    this._listenCustomizationSave();
    this._setSwitchers();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['activeColor'] || changes['activeTheme'] || changes['activeFont']) {
      this._setSwitchers();
    }
  }

  switchColor(colorOption: ColorI) {
    this.styles['--accentColor'] = colorOption.primaryColor;
    this.styles['--secondaryColor'] = colorOption.secondaryColor;
    this.styles['--accentColorShade'] = `${colorOption.primaryColor}0A`;
    this.styles['--accentColorShade2'] = `${colorOption.primaryColor}1A`;
    this.activeColor = colorOption.title;
    this._activeColor = colorOption;
    this.builderInteractionsService.activeColor.next(this._activeColor);
  }

  switchTheme(themeOption: { title: string, class: string }) {
    this.theme = themeOption.class;
    this.activeTheme = themeOption.title;
    this.builderInteractionsService.activeTheme.next(this.activeTheme);
  }

  switchFont(fontOption: string) {
    this.styles["--mainFontFamily"] = fontOption;
    this.activeFont = fontOption;
    this.builderInteractionsService.activeFont.next(this.activeFont);
  }

  selectOption(option) {
    if (this.switchers[0].optionsList.includes(option)) {
      this.switchTheme(option);
      this.selectedTheme = option;
      option?.class && this.selectFont(option?.class);
    } else if (this.switchers[1].optionsList.some(item => item.value === option)) {
      this.switchFont(option);
      this.selectedFont = option;
    } else if (this.switchers[2].optionsList.includes(option)) {
      this.switchColor(option);
      this.selectedColor = option;
    }
    this.builderInteractionsService.saveLocalState.next();
  }

  selectFont(theme) {
    const fonts = {
      classic: 'Georgia',
      flat: 'PT Sans',
      material: 'Montserrat',
      minimalistic: 'Lexend',
      soft: 'Poppins'
    };

    this.selectedFont = fonts[theme] || 'Georgia';
    this.switchFont(this.selectedFont);
  }

  public searchOptions($event) {
    if ($event === '') {
      this.optionsFontList = this.switchers[1].optionsList;
      return this.optionsFontList;
    }
    const v = $event.toLocaleLowerCase();

    const searchedResult = this.switchers[1].optionsList
      .map(option => {
        const labelLowerCase = option.label.toLocaleLowerCase();
        const index = labelLowerCase.indexOf(v);

        if (index !== -1) {
          option.matchIndex = index;
          return option;
        }
      })
      .filter(option => option)  // Remove undefined options
      .sort((a, b) => {
        if (a.matchIndex === b.matchIndex) {
          return a.label.localeCompare(b.label);
        }
        return a.matchIndex - b.matchIndex;
      });

    if (v) {
      this.optionsFontList = searchedResult.length ? searchedResult : this.switchers[1].optionsList;
    }
  }

  public resetDropdownOptions(): void {
    this.optionsFontList = this.switchers[1].optionsList;
  }

  private _setSwitchers() {
    const defaultSelectedColor = WEBSITE_CUSTOMIZATIONS.colors.find(color => color.title === this.activeColor);
    const defaultSelectedTheme = WEBSITE_CUSTOMIZATIONS.themes.find(theme => theme.title === this.activeTheme);
    this.switchers = [
      {
        id: SwitcherIds.THEME,
        icon: 'db-icon-ds-theme',
        tooltipTitle: 'Styles',
        hasSearch: false,
        optionsList: this.isEcommerce ? WEBSITE_CUSTOMIZATIONS.themes.filter(
          theme => theme.title !== 'Material' && theme.title !== 'Soft'
        ) : WEBSITE_CUSTOMIZATIONS.themes,
        defaultSelected: defaultSelectedTheme,
      },
      {
        id: SwitcherIds.FONT,
        icon: 'db-icon-ds-font',
        tooltipTitle: 'Fonts',
        hasSearch: true,
        optionsList: this.supportedFonts.map(font => ({
          label: font,
          value: font
        })),
        defaultSelected: this.supportedFonts.find(font => font === this.activeFont)
          ? {label: this.activeFont, value: this.activeFont}
          : null
      },
      {
        id: SwitcherIds.COLOR,
        icon: 'db-icon-ds-color',
        tooltipTitle: 'Colors',
        hasSearch: false,
        optionsList: WEBSITE_CUSTOMIZATIONS.colors,
        defaultSelected: defaultSelectedColor
      }
    ];
    console.log(WEBSITE_CUSTOMIZATIONS.colors);
  }

  private _listenDefaultColorChange() {
    this.builderInteractionsService.defaultColor
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        take(2)
      ).subscribe((color) => {
      this._activeColor = color;
      this.activeColor = color.title;
      this.activeFont = this.builderInteractionsService.activeFont.value;
      this.activeTheme = this.builderInteractionsService.activeTheme.value;
      this.switchColor(this._activeColor);
      this.switchFont(this.activeFont);
      this.switchTheme({
        title: CustomizationThemesEnum[this.activeTheme.toLowerCase()],
        class: this.activeTheme.toLowerCase()
      });
    });
  }

  private _listenCustomizationSave() {
    this.builderInteractionsService.saveCustomizationChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef)
      ).subscribe(() => {
      this.builderInteractionsService.activeColor.next(this._activeColor);
      this.builderInteractionsService.activeTheme.next(this.activeTheme);
      this.builderInteractionsService.activeFont.next(this.activeFont);
      this.builderInteractionsService.saveLocalState.next();
    });
  }
}
